import {
  State,
  appLogout,
  persistReducer,
  createAsyncThunk,
  createSlice,
  createSelector,
  AuthenticationStatus,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { CategoriesResponse, Category } from '../shared/categories.types';
import {
  CategoriesState,
  UpdateGameInFavoriteCategories,
} from './categories.types';
import CategoriesService from '../../services/CategoriesService/CategoriesService';
import { Platform } from 'react-native';

export const fetchCategories = createAsyncThunk<
  CategoriesResponse,
  AuthenticationStatus
>('categories/fetch', async (authStatus) =>
  authStatus === 'authenticated'
    ? await CategoriesService.getCategories()
    : await CategoriesService.getPublicCategories(),
);

const initialState: CategoriesState = {
  authenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
  unauthenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
};

const categories = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    updateGameInFavoriteCategories(
      state,
      { payload }: UpdateGameInFavoriteCategories,
    ) {
      const { favoriteCategoriesIds, gameId, type } = payload;

      for (const categoryId of favoriteCategoriesIds) {
        if (type === 'FAVORITE') {
          state.authenticated.byId[categoryId].games.push({ id: gameId });
        } else if (type === 'UNFAVORITE') {
          state.authenticated.byId[categoryId].games = state.authenticated.byId[
            categoryId
          ].games.filter((game) => game.id !== gameId);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCategories.fulfilled, (state, { meta, payload }) => {
      const { categories } = payload;
      state[meta.arg].dataState = 'fulfilled';
      state[meta.arg].byId = {};
      state[meta.arg].ids = [];

      categories.forEach((category, i) => {
        if (!state[meta.arg].ids.includes(category.id)) {
          state[meta.arg].ids.splice(i, 0, category.id);
        }

        state[meta.arg].byId[category.id] = category;
      });
    });
    builder.addCase(fetchCategories.pending, (state, { meta }) => {
      state[meta.arg].dataState =
        state[meta.arg].dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });
    builder.addCase(fetchCategories.rejected, (state, { meta }) => {
      state[meta.arg].dataState = 'rejected';
    });
    builder.addCase(appLogout, () => initialState);
  },
});

export const { updateGameInFavoriteCategories } = categories.actions;

const persistConfig = {
  key: 'categories',
  storage: AsyncStorage,
  whitelist: Platform.OS === 'web' ? [] : ['authenticated'],
  version: 2,
};

const persistedReducer = persistReducer(persistConfig, categories.reducer);

export const withCategories = injectSlice({
  reducerPath: 'categories',
  reducer: persistedReducer,
});

export const selectCategories = withCategories.selector(
  (state) => state.categories[state.app.authenticationStatus].byId,
);

export const selectCategoryIds = withCategories.selector(
  (state) => state.categories[state.app.authenticationStatus].ids,
);

export const selectFavoriteCategoriesIds = createSelector(
  [selectCategories, selectCategoryIds],
  (categories, categoryIds) =>
    categoryIds.filter((id) => {
      const category = categories[id];
      return category.gamesSelectionType === 'Favorites';
    }),
);

export const selectLobbyCategoryIds = createSelector(
  [selectCategories, selectCategoryIds],
  (categories, categoryIds) =>
    categoryIds.filter((id) => categories[id].showOnLobby),
);

export const selectCategory = createSelector(
  [
    (_: State, id: Category['id'] | Category['slug']) => id,
    selectCategories,
    selectCategoryIds,
  ],
  (id, categories, categoryIds) => {
    if (typeof id === 'number' && categories[id]) {
      return categories[id];
    }

    const categoryId = categoryIds.find(
      (catId: Category['id']) => categories[catId].slug === id,
    );
    return categoryId !== undefined ? categories[categoryId] : undefined;
  },
);

export const selectCategoriesState = withCategories.selector(
  (state) => state.categories[state.app.authenticationStatus].dataState,
);

declare global {
  interface LazyLoadedSlices {
    categories: ReturnType<typeof persistedReducer>;
  }
}
